
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Message from 'primevue/message';
import Panel from 'primevue/panel';

import Footer from '@/components/Footer.vue';
import LoginFields from '@/components/login/LoginFields.vue';
import RegisterFields from '@/components/login/RegisterFields.vue';
import RequestPasswordResetFields from '@/components/login/RequestPasswordResetFields.vue';
import ResetPasswordFields from '@/components/login/ResetPasswordFields.vue';
import SelfRegisterFields from '@/components/login/SelfRegisterFields.vue';
import UpdatePasswordFields from '@/components/login/UpdatePasswordFields.vue';
import ValidateSelfRegistrationFields from '@/components/login/ValidateSelfRegistrationFields.vue';
import { useAuthFlow, AuthFlowMode } from '@/composables/useAuthFlow';
import { useLoginService } from '@/composables/useLoginService';
import { useStatusService } from '@/composables/useStatusService';
import store from '@/store';
import LoginResponse from '@/types/response/login';
import Styles from '@/types/styles';
import SessionHandoffPayload from '@/types/SessionHandoffPayload';

const RESET_PASSWORD = 'RESET_PASSWORD';
const REGISTER_MODES = [
  AuthFlowMode.Register,
  AuthFlowMode.ValidateRegistration,
  AuthFlowMode.SelfRegister,
  AuthFlowMode.SelfRegisterEmailSent,
];
const RESET_PASSWORD_MODES = [AuthFlowMode.RequestPasswordReset, AuthFlowMode.ResetPassword];
const RESET_FLOW = 'reset-password';
const SELF_REGISTER_FLOW = 'self-register';

export default defineComponent({
  name: 'LoginForm',
  components: {
    Button,
    Dialog,
    Footer,
    LoginFields,
    Message,
    Panel,
    RegisterFields,
    RequestPasswordResetFields,
    ResetPasswordFields,
    SelfRegisterFields,
    UpdatePasswordFields,
    ValidateSelfRegistrationFields,
  },
  setup() {
    const styles = computed(() => store.getters['styles/getStyles'] as Styles);
    const {
      currentMode,
      allowPasswordReset,
      allowSelfRegistration,
      useEmailSelfRegistration,
      buttonLabel,
      selfRegistrationFields,
      setMode,
    } = useAuthFlow();
    const {
      isLoading: loginLoading,
      error: serviceError,
      passwordError,
      showPasswordValidationError,
      login,
      register,
      updatePassword,
      requestPasswordReset,
      portalResetPassword,
      validateSelfRegistration,
      selfRegister,
      requestSelfRegistrationEmail,
      completeSelfRegistration,
    } = useLoginService();
    const { error: statusError, checkStatus } = useStatusService();

    const activeFlow = ref<{ submit: () => boolean; setInitialState?: (data: Record<string, string>) => void } | null>(null);
    const loginError = ref('');
    const infoMessage = ref('');
    // Shared state across mode transitions (login→updatePassword, validate→selfRegister, URL→resetPassword)
    const nonce = ref('');
    const resetEmail = ref('');
    const completionFlow = ref(RESET_FLOW);
    let statusIntervalId: number | undefined;
    const showSubmitButton = computed(() => currentMode.value !== AuthFlowMode.SelfRegisterEmailSent);
    const submitButtonLabel = computed(() => {
      if (currentMode.value === AuthFlowMode.ResetPassword && completionFlow.value === SELF_REGISTER_FLOW) {
        return 'Complete Registration';
      }
      return buttonLabel.value;
    });
    const showTopInfoMessage = computed(() =>
      !!infoMessage.value && currentMode.value !== AuthFlowMode.SelfRegisterEmailSent
    );

    const passwordErrorDialogLines = computed(() => passwordError.value.split('\n').filter(Boolean));

    const loginActive = computed(() => ({
      grey: currentMode.value !== AuthFlowMode.Login,
      white: currentMode.value === AuthFlowMode.Login,
    }));
    const registerActive = computed(() => ({
      grey: !REGISTER_MODES.includes(currentMode.value),
      white: REGISTER_MODES.includes(currentMode.value),
    }));
    const resetPasswordActive = computed(() => ({
      grey: !RESET_PASSWORD_MODES.includes(currentMode.value),
      white: RESET_PASSWORD_MODES.includes(currentMode.value),
    }));

    const applyServiceError = () => {
      if (serviceError.value) {
        loginError.value = serviceError.value;
      }
    };

    const setFlow = (mode: AuthFlowMode) => {
      setMode(mode);
      loginError.value = '';
      infoMessage.value = '';
    };

    const handleSubmit = () => {
      loginError.value = '';
      activeFlow.value?.submit();
    };

    const onLogin = async (payload: { email: string; password: string }) => {
      const response = await login(payload.email, payload.password, styles.value.client_name);

      if (!response) {
        applyServiceError();
        return;
      }

      if (response.error && response.action !== RESET_PASSWORD) {
        loginError.value = response.error;
        return;
      }

      if (response.action === RESET_PASSWORD) {
        nonce.value = response.nonce;
        setMode(AuthFlowMode.UpdatePassword);
        loginError.value = response.error || 'You must update your password before proceeding.';
        await nextTick();
        activeFlow.value?.setInitialState?.({
          email: payload.email,
          oldPassword: payload.password,
        });
        return;
      }

      await doLogin(response);
    };

    const onRegister = async (payload: { email: string; password: string }) => {
      const response = await register(payload.email, payload.password, styles.value.client_name);

      if (!response) {
        applyServiceError();
        return;
      }

      if (response.error) {
        loginError.value = response.error;
        return;
      }

      await doLogin(response);
    };

    const onUpdatePassword = async (payload: { email: string; oldPassword: string; password: string }) => {
      const response = await updatePassword(
        payload.email,
        payload.oldPassword,
        payload.password,
        styles.value.client_name,
        nonce.value
      );

      if (!response) {
        applyServiceError();
        return;
      }

      if (response.action === RESET_PASSWORD) {
        nonce.value = response.nonce;
      }

      await doLogin(response);
    };

    const onRequestPasswordReset = async (payload: { email: string }) => {
      const response = await requestPasswordReset(payload.email, styles.value.client_name);

      if (!response) {
        applyServiceError();
        return;
      }

      if (response.error) {
        loginError.value = response.error;
        infoMessage.value = '';
      }

      if (response.message) {
        infoMessage.value = response.message;
        loginError.value = '';
      }
    };

    const onResetPassword = async (payload: { password: string }) => {
      if (!resetEmail.value) {
        loginError.value = 'The reset link is missing your email address. Please request a new link.';
        return;
      }

      const response = completionFlow.value === SELF_REGISTER_FLOW
        ? await completeSelfRegistration(
          resetEmail.value,
          payload.password,
          nonce.value,
          styles.value.client_name
        )
        : await portalResetPassword(
          resetEmail.value,
          payload.password,
          styles.value.client_name,
          nonce.value
        );

      if (!response) {
        applyServiceError();
        return;
      }

      if (response.error) {
        loginError.value = response.error;
        infoMessage.value = '';
      }

      if (response.message) {
        infoMessage.value = response.message;
        loginError.value = '';
      }

      if (!response.error) {
        if (response.user || response.contact) {
          await doLogin(response);
        } else {
          setFlow(AuthFlowMode.Login);
        }
      }
    };

    const onValidateSelfRegistration = async (payload: { fields: Record<string, string> }) => {
      const response = await validateSelfRegistration(payload.fields, styles.value.client_name);

      if (!response) {
        applyServiceError();
        return;
      }

      if (response.error) {
        loginError.value = response.error;
        return;
      }

      nonce.value = response.nonce;
      setMode(AuthFlowMode.SelfRegister);
      infoMessage.value = 'Identity verified. Please complete your account details.';
      loginError.value = '';
    };

    const onSelfRegister = async (payload: { firstName: string; lastName: string; email: string; password?: string }) => {
      const response = useEmailSelfRegistration.value
        ? await requestSelfRegistrationEmail(
          payload.firstName,
          payload.lastName,
          payload.email,
          nonce.value,
          styles.value.client_name
        )
        : await selfRegister(
          payload.firstName,
          payload.lastName,
          payload.email,
          payload.password || '',
          nonce.value,
          styles.value.client_name
        );

      if (!response) {
        applyServiceError();
        return;
      }

      if (response.error) {
        loginError.value = response.error;
        infoMessage.value = '';
        return;
      }

      if (useEmailSelfRegistration.value) {
        setMode(AuthFlowMode.SelfRegisterEmailSent);
        infoMessage.value = response.message || 'If your registration can be completed by email, a message has been sent with a link to continue.';
        loginError.value = '';
        return;
      }

      await doLogin(response);
    };

    const handleLoginClick = () => {
      setFlow(AuthFlowMode.Login);
    };

    const handleRegisterClick = () => {
      setFlow(allowSelfRegistration.value ? AuthFlowMode.ValidateRegistration : AuthFlowMode.Register);
    };

    const handleResetPasswordClick = () => {
      setFlow(AuthFlowMode.RequestPasswordReset);
    };

    const doLogin = async (response: LoginResponse) => {
      const destinationUrl = getUrl(response);
      const shouldForwardSelfRegistrationContext =
        !!response.contact &&
        allowSelfRegistration.value &&
        selfRegistrationFields.value.length > 0;

      if (response.user?.commands_rights && !Object.keys(response.user.commands_rights).length) {
        // this is a hack to reduce backend integrations work and maintain forward/backwards compatibility with new commands rights feature
        response.user.commands_rights = {};
        response.user.commands = [];
      }

      const sessionPayload: SessionHandoffPayload = {
        name: styles.value.client_name,
        user: response.user ? response.user : response.contact,
        customers: response.custs,
        subKey: response.subscription_key,
        roverJwt: response.token,
        subscriptionClient: response.subscriptionClient,
        loginUrl: '' + process.env.VUE_APP_LOGIN_URL,
        refresh_token: response.refresh_token,
        expires_in: response.expires_in,
        embeddedAppOrigins: response.embedded_app_origins,
        ...(shouldForwardSelfRegistrationContext
          ? {
            forwardedContext: {
              allowSelfRegistration: true,
              selfRegistrationFields: selfRegistrationFields.value,
            },
          }
          : {}),
        theme: {
          primaryColor: styles.value.primary_color,
          backgroundColor: styles.value.background_color,
          secondaryColor: styles.value.secondary_color,
          sidebarTextColor: styles.value.sidebar_text_color,
          logo: styles.value.logo,
          customLogo: styles.value.custom_logo,
          title: styles.value.client_name,
          sidebarBGColor: styles.value.sidebar_bg_color,
          tabLogo: styles.value.tab_logo,
        },
      };

      // Deliberately no fallback to a URL-embedded session payload. That payload now carries
      // refresh_token, so putting it in the querystring would expose a 30-day credential via
      // browser history, Referer headers and proxy/APIM logs -- strictly worse than the
      // short-lived token this handoff was introduced to remove. A silent fallback would also
      // let a misconfigured or undeployed handoff endpoint downgrade every login indefinitely
      // with no visible symptom, so a failure here is surfaced to the user instead.
      try {
        const code = await generateHandoffCode(
          response.token,
          response.subscription_key ?? '',
          sessionPayload
        );
        location.replace(`${destinationUrl}?${new URLSearchParams({ code })}`);
      } catch (error) {
        console.error('[LoginForm] Handoff code generation failed:', error);
        loginError.value =
          'We could not complete your sign in. Please try again, or contact support if the problem continues.';
      }
    };

    const generateHandoffCode = async (
      roverJwt: string,
      subscriptionKey: string,
      sessionPayload: SessionHandoffPayload
    ): Promise<string> => {
      const apiUrl = (process.env.VUE_APP_ABSTRACTION_API || '').replace(/\/+$/, '');
      if (!apiUrl) {
        throw new Error('VUE_APP_ABSTRACTION_API is not configured');
      }

      // GenerateHandoffCode is published only in the TokenlessLogin swagger group, so APIM
      // exposes it under the /tokenless API. Locally the app is hit directly, with no APIM
      // path prefix. Mirrors the base URL rule in @/container/ServiceConfig.
      const baseUrl = `${apiUrl}${apiUrl.includes('localhost') ? '' : '/tokenless'}`;

      const response = await fetch(`${baseUrl}/v2/GenerateHandoffCode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Ocp-Apim-Subscription-Key': subscriptionKey,
          'X-Rover-Jwt': roverJwt,
        },
        body: JSON.stringify({ sessionPayload }),
      });

      if (!response.ok) {
        throw new Error(`GenerateHandoffCode HTTP ${response.status}`);
      }

      const data = await response.json();
      if (!data.code) {
        throw new Error('GenerateHandoffCode returned no code');
      }

      return data.code as string;
    };

    const getUrl = (response: LoginResponse) => {
      if (response.user) {
        return new URL('' + process.env.VUE_APP_WEB_CLIENT_URL);
      }

      if (response.contact) {
        return new URL('' + process.env.VUE_APP_CUSTOMER_PORTAL_URL);
      }

      throw new Error('No valid user');
    };

    onMounted(async () => {
      await checkStatus();
      if (statusError.value) {
        loginError.value = statusError.value;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const nonceParam = urlParams.get('nonce');
      const emailParam = urlParams.get('email');
      const flowParam = urlParams.get('flow');

      if (nonceParam) {
        nonce.value = nonceParam;
        if (emailParam) {
          resetEmail.value = emailParam;
        }
        completionFlow.value = flowParam === SELF_REGISTER_FLOW ? SELF_REGISTER_FLOW : RESET_FLOW;
        setMode(AuthFlowMode.ResetPassword);
        infoMessage.value = completionFlow.value === SELF_REGISTER_FLOW
          ? 'Please set your password to complete your registration.'
          : 'Please enter your new password.';
      }

      try {
        if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
          const currentUrl = new URL(window.location.href);
          const searchParams = currentUrl.searchParams;
          searchParams.delete('nonce');
          searchParams.delete('email');
          searchParams.delete('flow');
          const remainingQuery = searchParams.toString();
          const newUrl =
            currentUrl.pathname +
            (remainingQuery ? '?' + remainingQuery : '') +
            currentUrl.hash;
          window.history.replaceState(null, document.title, newUrl);
        }
      } catch {
        // no-op
      }

      statusIntervalId = window.setInterval(async () => {
        await checkStatus();
        if (statusError.value) {
          loginError.value = statusError.value;
        }
      }, 240000);
    });

    onBeforeUnmount(() => {
      if (statusIntervalId) {
        window.clearInterval(statusIntervalId);
      }
    });

    return {
      AuthFlowMode,
      activeFlow,
      allowPasswordReset,
      buttonLabel,
      currentMode,
      handleLoginClick,
      handleRegisterClick,
      handleResetPasswordClick,
      handleSubmit,
      infoMessage,
      loginActive,
      loginError,
      loginLoading,
      showTopInfoMessage,
      showSubmitButton,
      submitButtonLabel,
      onLogin,
      onRegister,
      onUpdatePassword,
      onRequestPasswordReset,
      onResetPassword,
      onValidateSelfRegistration,
      onSelfRegister,
      passwordError,
      passwordErrorDialogLines,
      registerActive,
      resetPasswordActive,
      selfRegistrationFields,
      showPasswordValidationError,
      styles,
      useEmailSelfRegistration,
    };
  },
});
